/**
 * Date-range field for the HR "Out of System" encode form.
 *
 * Same interaction as the /apply Dates step: a trigger button summarising the
 * selection ("06 Aug 2026 - 11 Aug 2026 · 4 working days") that expands the
 * two-month RangeCalendar inline. Unlike /apply there is no booking window or
 * min date — HR encodes leave that was approved offline, often in the past.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { addDays, format, isWeekend, parseISO } from 'date-fns'
import type { LeaveTypeId } from '@lms/shared-types'

import { RangeCalendar } from '../../components/employee/RangeCalendar'
import { Button } from '../../components/primitives/Button'
import { FadeIn } from '../../components/primitives/FadeIn'
import { Icon } from '../../components/primitives/Icon'
import { LEAVE_TYPE_STYLE } from '../../lib/leave-type-palette'
import { useViewport } from '../../lib/use-viewport'

export interface OosDateRangeFieldProps {
  startDate: string | null
  endDate: string | null
  onChange: (startDate: string | null, endDate: string | null) => void
  publicHolidays: ReadonlyArray<string>
  holidayNames?: ReadonlyArray<{ date: string; name: string }>
  /** Tints the calendar to the selected leave type, as on /apply. */
  leaveType?: LeaveTypeId | undefined
}

function countWorkingDays(
  start: string,
  end: string,
  holidays: ReadonlySet<string>,
): number {
  let count = 0
  for (let d = parseISO(start); format(d, 'yyyy-MM-dd') <= end; d = addDays(d, 1)) {
    if (!isWeekend(d) && !holidays.has(format(d, 'yyyy-MM-dd'))) count += 1
  }
  return count
}

function rangeLabel(start: string | null, end: string | null): string {
  if (!start) return ''
  const startLabel = format(parseISO(start), 'dd MMM yyyy')
  if (!end) return `${startLabel} - …`
  if (end === start) return startLabel
  return `${startLabel} - ${format(parseISO(end), 'dd MMM yyyy')}`
}

export function OosDateRangeField({
  startDate,
  endDate,
  onChange,
  publicHolidays,
  holidayNames,
  leaveType,
}: OosDateRangeFieldProps): JSX.Element {
  const { isDesktop } = useViewport()
  const [open, setOpen] = useState(false)
  // Reopening onto an existing selection → the next click starts a fresh pick
  // instead of extending the committed range (mirrors /apply).
  const [freshPick, setFreshPick] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const holidaySet = useMemo(() => new Set(publicHolidays), [publicHolidays])
  const label = rangeLabel(startDate, endDate)
  const days =
    startDate && endDate ? countWorkingDays(startDate, endDate, holidaySet) : null

  function handleRange(from: string, to: string): void {
    if (freshPick) {
      setFreshPick(false)
      const clicked = to !== '' ? to : from
      onChange(clicked, clicked)
      return
    }
    onChange(from || null, to || null)
    // First click sets a 1-day range and stays open; a later date or a
    // re-click on the same selected day completes the pick and closes.
    const reclickSameDay = from !== '' && from === to && startDate === from && endDate === to
    if ((from && to && to > from) || reclickSameDay) setOpen(false)
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        data-testid="oos-dates-trigger"
        onClick={() => {
          if (!open && startDate) setFreshPick(true)
          setOpen((o) => !o)
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 14px',
          minHeight: 44,
          background: 'var(--surface)',
          border: `1px solid ${open ? 'var(--steel-blue)' : 'var(--border)'}`,
          boxShadow: open ? '0 0 0 3px var(--baby-blue-tint)' : 'none',
          borderRadius: 8,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 13,
          color: 'var(--text)',
          textAlign: 'left',
          transition: 'border-color .12s, box-shadow .12s',
        }}
      >
        <Icon name="calendar_month" size={18} color="var(--text-3)" aria-hidden="true" />
        {label ? (
          <span style={{ fontWeight: 500 }}>
            {label}
            {days !== null ? (
              <>
                {' · '}
                <strong style={{ fontWeight: 700 }}>
                  {days} {days === 1 ? 'working day' : 'working days'}
                </strong>
              </>
            ) : null}
          </span>
        ) : (
          <span style={{ color: 'var(--text-3)' }}>Set dates</span>
        )}
        <span
          style={{
            marginLeft: 'auto',
            color: 'var(--text-3)',
            display: 'inline-flex',
            alignItems: 'center',
            transition: 'transform .15s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <Icon name="expand_more" size={16} aria-hidden="true" />
        </span>
      </button>

      {open ? (
        <FadeIn rise={4} duration="fast">
          <div
            role="group"
            aria-label="Pick dates"
            data-testid="oos-dates-popover"
            style={{
              marginTop: 8,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: 12,
            }}
          >
            <RangeCalendar
              from={startDate ?? ''}
              to={endDate ?? ''}
              onChange={handleRange}
              publicHolidays={publicHolidays}
              {...(holidayNames ? { holidayNames } : {})}
              oneClickSingleDay
              mobile={!isDesktop}
              {...(leaveType ? { accent: LEAVE_TYPE_STYLE[leaveType] } : {})}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
              }}
            >
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                data-testid="oos-dates-clear"
                onClick={() => onChange(null, null)}
                disabled={!startDate}
              >
                Clear
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                data-testid="oos-dates-done"
                onClick={() => setOpen(false)}
              >
                Done
              </Button>
            </div>
          </div>
        </FadeIn>
      ) : null}
    </div>
  )
}
